import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import { stageState } from "../../lib/stageState";

/* ------------------------------------------------------------------ helpers */
// Monochrome theme: the accent is pure light.
const GOLD = "#F5F7FA";
const GOLD_DEEP = "#AEB5C0";
const clamp01 = (v) => Math.min(1, Math.max(0, v));
const smooth = (t) => t * t * (3 - 2 * t);
// eased phase of p within [a, b]
const seg = (p, a, b) => smooth(clamp01((p - a) / (b - a)));
const lerp = (a, b, t) => a + (b - a) * t;
const damp = (cur, target, lambda, dt) => THREE.MathUtils.damp(cur, target, lambda, dt);
// ambient time: frozen under prefers-reduced-motion (scroll still drives everything)
const T = (clock) => (stageState.reduced ? 0 : clock.elapsedTime);

function makeGlowTexture() {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.25, "rgba(255,255,255,0.55)");
  g.addColorStop(0.6, "rgba(255,255,255,0.12)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

function useGlowTexture() {
  const tex = useMemo(() => makeGlowTexture(), []);
  useEffect(() => () => tex.dispose(), [tex]);
  return tex;
}

/* ------------------------------------------------------------------ camera */
const CAM_KEYS = [
  { x: 0, y: 0.2, z: 9.5, lx: 0, ly: 0 }, // hero
  { x: 0, y: 0.1, z: 8.2, lx: 0, ly: 0 }, // pull
  { x: 0.3, y: 0.2, z: 8.6, lx: 0.2, ly: 0 }, // reconcile
  { x: 1.1, y: 0.4, z: 6.9, lx: 1.2, ly: -0.1 }, // human
  { x: 0, y: 0.1, z: 6.2, lx: 0, ly: 0.1 }, // output
  { x: 0, y: 0.1, z: 6.2, lx: 0, ly: 0.1 },
];

function CameraRig() {
  const { camera, viewport } = useThree();
  const look = useRef(new THREE.Vector3());
  useFrame((_, dt) => {
    const lift = viewport.aspect < 1 ? 0.95 : viewport.aspect < 1.3 ? 0.45 : 0;
    const p = stageState.p;
    const i = Math.min(4, Math.floor(p));
    const t = smooth(clamp01(p - i));
    const a = CAM_KEYS[i];
    const b = CAM_KEYS[i + 1];
    const tx = lerp(a.x, b.x, t) + stageState.pointerX * 0.35;
    const ty = lerp(a.y, b.y, t) + stageState.pointerY * 0.2 - lift;
    const tz = lerp(a.z, b.z, t) + (viewport.aspect < 1 ? 1.6 : 0);
    camera.position.x = damp(camera.position.x, tx, 2.2, dt);
    camera.position.y = damp(camera.position.y, ty, 2.2, dt);
    camera.position.z = damp(camera.position.z, tz, 2.2, dt);
    look.current.x = damp(look.current.x, lerp(a.lx, b.lx, t), 2.2, dt);
    look.current.y = damp(look.current.y, lerp(a.ly, b.ly, t) - lift, 2.2, dt);
    camera.lookAt(look.current.x, look.current.y, 0);
  });
  return null;
}

/* ------------------------------------------------------------------ dust */
function Dust({ count = 420 }) {
  const ref = useRef();
  const tex = useGlowTexture();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 22;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 12;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 10 - 2;
    }
    return arr;
  }, [count]);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    ref.current.rotation.y = T(clock) * 0.012;
    ref.current.position.y = Math.sin(T(clock) * 0.15) * 0.2;
  });
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        map={tex}
        color={GOLD}
        size={0.07}
        transparent
        opacity={0.35}
        depthWrite={false}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

/* ------------------------------------------------------------------ fragments (hero → pull) */
function Fragments({ count = 190 }) {
  const mesh = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const tmp = useMemo(() => new THREE.Vector3(), []);
  const data = useMemo(() => {
    const list = [];
    for (let i = 0; i < count; i++) {
      const r = 2.2 + Math.random() * 5.2;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const chaos = new THREE.Vector3(
        r * Math.sin(phi) * Math.cos(theta),
        r * Math.sin(phi) * Math.sin(theta) * 0.62,
        r * Math.cos(phi) * 0.7 - 1.2
      );
      list.push({
        chaos,
        angle: theta,
        rot: [Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI],
        spin: [(Math.random() - 0.5) * 0.35, (Math.random() - 0.5) * 0.35, (Math.random() - 0.5) * 0.25],
        scale: 0.55 + Math.random() * 0.9,
        offset: Math.random() * Math.PI * 2,
        delay: Math.random() * 0.55,
        tone: Math.random(),
      });
    }
    return list;
  }, [count]);

  useEffect(() => {
    if (!mesh.current) return;
    const c = new THREE.Color();
    data.forEach((d, i) => {
      // mostly graphite; ~12% catch a faint gold tint, like paper catching light
      if (d.tone > 0.88) c.set("#5a606b");
      else if (d.tone > 0.55) c.set("#2a313c");
      else c.set("#1b2029");
      mesh.current.setColorAt(i, c);
    });
    mesh.current.instanceColor.needsUpdate = true;
  }, [data]);

  useFrame(({ clock }) => {
    if (!mesh.current) return;
    const t = T(clock);
    const p = stageState.p;
    const pull = seg(p, 0.85, 1.85);
    const gone = seg(p, 1.75, 2.15);
    const intro = seg(p, -0.2, 0.05); // fully visible on load

    for (let i = 0; i < data.length; i++) {
      const d = data[i];
      const pi = smooth(clamp01((pull - d.delay * 0.5) / (1 - d.delay * 0.5)));
      // gentle drift while in chaos
      const dx = Math.sin(t * 0.28 + d.offset) * 0.28;
      const dy = Math.cos(t * 0.22 + d.offset * 1.3) * 0.24;
      // spiral inward toward the core
      const ang = d.angle + pi * Math.PI * 2.4 + t * 0.15 * pi;
      const rad = d.chaos.length() * (1 - pi) + 0.18;
      tmp.set(Math.cos(ang) * rad, d.chaos.y * (1 - pi) * 0.9 + dy * (1 - pi), Math.sin(ang) * rad * 0.55);
      const px = lerp(d.chaos.x + dx, tmp.x, pi);
      const py = lerp(d.chaos.y + dy, tmp.y, pi);
      const pz = lerp(d.chaos.z, tmp.z, pi);
      dummy.position.set(px, py, pz);
      dummy.rotation.set(
        d.rot[0] + t * d.spin[0] + pi * 2.5,
        d.rot[1] + t * d.spin[1] + pi * 1.5,
        d.rot[2] + t * d.spin[2]
      );
      const s = Math.max(0.0001, d.scale * (1 - gone) * (1 - 0.55 * pi) * (0.2 + 0.8 * intro));
      dummy.scale.set(s, s * 1.32, 1);
      dummy.updateMatrix();
      mesh.current.setMatrixAt(i, dummy.matrix);
    }
    mesh.current.instanceMatrix.needsUpdate = true;
    mesh.current.visible = gone < 0.999;
  });

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, count]} frustumCulled={false}>
      <planeGeometry args={[0.4, 0.52]} />
      <meshStandardMaterial metalness={0.7} roughness={0.38} side={THREE.DoubleSide} />
    </instancedMesh>
  );
}

/* ------------------------------------------------------------------ engine core (pull) */
function Core() {
  const group = useRef();
  const mat = useRef();
  const light = useRef();
  const shell = useRef();
  const shell2 = useRef();
  const glow = useRef();
  const tex = useGlowTexture();

  useFrame(({ clock }) => {
    if (!group.current) return;
    const p = stageState.p;
    const t = T(clock);
    const appear = seg(p, 0.75, 1.5);
    const leave = seg(p, 1.95, 2.55);
    const s = appear * (1 - leave);
    group.current.visible = s > 0.002;
    group.current.scale.setScalar(Math.max(0.0001, s));
    group.current.position.z = -leave * 3;
    const breathe = 0.86 + Math.sin(t * 1.3) * 0.14;
    const charge = seg(p, 1.1, 1.85);
    mat.current.emissiveIntensity = (0.35 + 1.15 * charge) * breathe;
    light.current.intensity = (6 + 26 * charge) * s * breathe;
    shell.current.rotation.y = t * 0.14;
    shell.current.rotation.x = t * 0.07;
    shell2.current.rotation.y = -t * 0.09;
    shell2.current.rotation.z = t * 0.05;
    glow.current.material.opacity = (0.18 + 0.4 * charge) * breathe * s;
    const gs = 3.2 + charge * 2.6;
    glow.current.scale.set(gs, gs, 1);
  });

  return (
    <group ref={group}>
      <mesh>
        <icosahedronGeometry args={[0.52, 4]} />
        <meshStandardMaterial
          ref={mat}
          color={GOLD_DEEP}
          emissive={GOLD}
          emissiveIntensity={1}
          roughness={0.28}
          metalness={0.85}
        />
      </mesh>
      <mesh ref={shell} scale={1.45}>
        <icosahedronGeometry args={[0.6, 1]} />
        <meshBasicMaterial color={GOLD} wireframe transparent opacity={0.22} />
      </mesh>
      <mesh ref={shell2} scale={1.9}>
        <icosahedronGeometry args={[0.6, 0]} />
        <meshBasicMaterial color="#8d97a8" wireframe transparent opacity={0.1} />
      </mesh>
      <sprite ref={glow} position={[0, 0, -0.2]}>
        <spriteMaterial map={tex} color={GOLD} transparent opacity={0.4} depthWrite={false} blending={THREE.AdditiveBlending} />
      </sprite>
      <pointLight ref={light} color={GOLD} distance={14} decay={2} intensity={0} />
    </group>
  );
}

/* ------------------------------------------------------------------ streams (reconcile) */
const STREAM_N = 64;
const STREAM_COLORS = ["#6f7a8c", "#9aa4b5", "#e6e9ee"];

function Stream({ k, exceptionsRef }) {
  const mesh = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const seeds = useMemo(
    () => Array.from({ length: STREAM_N }, () => ({ jitter: Math.random() * 0.5 - 0.25, wob: Math.random() * Math.PI * 2 })),
    []
  );

  useFrame(({ clock }) => {
    if (!mesh.current) return;
    const p = stageState.p;
    const t = T(clock);
    const appear = seg(p, 1.9, 2.4);
    const converge = seg(p, 2.25, 2.95);
    const recede = seg(p, 3.0, 3.4); // move back during The Human
    const fade = seg(p, 3.9, 4.35);
    const vis = appear * (1 - fade);
    mesh.current.visible = vis > 0.002;
    const baseY = (k - 1) * 1.35;
    const phase = (k * Math.PI * 2) / 3;
    for (let i = 0; i < STREAM_N; i++) {
      const u = i / (STREAM_N - 1);
      const x = lerp(-6.5, 6.5, u) - 0.6;
      // chaotic separate streams
      const chaosY = baseY + Math.sin(x * 1.7 + t * 0.7 + seeds[i].wob) * 0.42 + seeds[i].jitter;
      const chaosZ = Math.cos(x * 1.1 + seeds[i].wob) * 0.6;
      // interlocked triple helix
      const helixY = Math.sin(x * 1.35 + t * 0.45 + phase) * 0.42;
      const helixZ = Math.cos(x * 1.35 + t * 0.45 + phase) * 0.42;
      const y = lerp(chaosY, helixY, converge);
      const z = lerp(chaosZ, helixZ, converge) - recede * 2.4;
      dummy.position.set(x * (0.3 + 0.7 * appear), y, z);
      dummy.rotation.set(t * 0.5 + i * 0.2, x * 0.5, 0);
      const s = (0.07 + 0.05 * Math.sin(u * Math.PI)) * vis * (1 - recede * 0.3);
      dummy.scale.setScalar(Math.max(0.0001, s));
      dummy.updateMatrix();
      mesh.current.setMatrixAt(i, dummy.matrix);
    }
    mesh.current.instanceMatrix.needsUpdate = true;
    mesh.current.material.emissiveIntensity = 0.15 + converge * 0.55;
  });

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, STREAM_N]} frustumCulled={false}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color={STREAM_COLORS[k]} emissive={STREAM_COLORS[k]} emissiveIntensity={0.2} metalness={0.8} roughness={0.35} />
    </instancedMesh>
  );
}

/* Exceptions drift aside; one becomes the "hero" exception that gets sealed. */
const EXCEPTION_COUNT = 6;
const HERO_EXC = new THREE.Vector3(1.55, -0.35, 0.6);

function Exceptions() {
  const mesh = useRef();
  const hero = useRef();
  const heroMat = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const data = useMemo(
    () =>
      Array.from({ length: EXCEPTION_COUNT }, (_, i) => ({
        from: new THREE.Vector3(lerp(-4, 4, Math.random()), (Math.random() - 0.5) * 2, 0),
        to: new THREE.Vector3(2.2 + Math.random() * 1.6, 1.0 + Math.random() * 1.6 - (i % 2) * 3.2, -0.6 + Math.random()),
        wob: Math.random() * Math.PI * 2,
      })),
    []
  );

  useFrame(({ clock }) => {
    if (!mesh.current || !hero.current) return;
    const p = stageState.p;
    const t = T(clock);
    const eject = seg(p, 2.45, 3.0);
    const fade = seg(p, 3.55, 3.95); // minor exceptions fade once the human takes over
    const heroFade = seg(p, 3.95, 4.3);
    const vis = eject * (1 - fade);
    mesh.current.visible = vis > 0.002;
    for (let i = 0; i < EXCEPTION_COUNT; i++) {
      const d = data[i];
      const e = smooth(clamp01((eject - i * 0.06) / (1 - i * 0.06)));
      dummy.position.lerpVectors(d.from, d.to, e);
      dummy.position.y += Math.sin(t * 0.6 + d.wob) * 0.08;
      dummy.rotation.set(t * 0.3 + d.wob, t * 0.2, 0);
      dummy.scale.setScalar(Math.max(0.0001, 0.11 * vis));
      dummy.updateMatrix();
      mesh.current.setMatrixAt(i, dummy.matrix);
    }
    mesh.current.instanceMatrix.needsUpdate = true;

    // hero exception: ejects, hovers, gets stamped (turns gold), rises away
    const heroVis = eject * (1 - heroFade);
    hero.current.visible = heroVis > 0.002;
    const stamped = seg(p, 3.48, 3.6);
    hero.current.position.set(
      lerp(-0.5, HERO_EXC.x, eject),
      lerp(0.3, HERO_EXC.y, eject) + Math.sin(t * 0.7) * 0.05 * (1 - stamped) + heroFade * 1.5,
      lerp(0, HERO_EXC.z, eject)
    );
    hero.current.rotation.y = t * 0.35 * (1 - stamped) + stamped * Math.PI * 0.25;
    hero.current.rotation.x = 0.5 * (1 - stamped);
    const hs = (0.26 + 0.05 * stamped) * heroVis;
    hero.current.scale.setScalar(Math.max(0.0001, hs));
    heroMat.current.color.set(stamped > 0.5 ? GOLD_DEEP : "#4b515c");
    heroMat.current.emissive.set(GOLD);
    heroMat.current.emissiveIntensity = stamped * 0.85 * (0.85 + Math.sin(t * 2) * 0.15);
  });

  return (
    <group>
      <instancedMesh ref={mesh} args={[undefined, undefined, EXCEPTION_COUNT]} frustumCulled={false}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#3c424c" metalness={0.6} roughness={0.6} />
      </instancedMesh>
      <mesh ref={hero}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial ref={heroMat} color="#4b515c" metalness={0.75} roughness={0.35} emissive={GOLD} emissiveIntensity={0} />
      </mesh>
    </group>
  );
}

/* ------------------------------------------------------------------ The Seal (human) */
function Seal() {
  const group = useRef();
  const light = useRef();
  const ring = useRef();
  const ring2 = useRef();
  const flare = useRef();
  const markMat = useRef();
  const tex = useGlowTexture();

  useFrame(({ clock }) => {
    if (!group.current) return;
    const p = stageState.p;
    const t = T(clock);
    const enter = seg(p, 2.95, 3.3);
    const descendRaw = clamp01((p - 3.32) / 0.2);
    const descend = descendRaw * descendRaw * descendRaw; // weighted drop
    const lift = seg(p, 3.68, 3.95);
    const fade = seg(p, 3.98, 4.3);
    const vis = enter * (1 - fade);
    group.current.visible = vis > 0.002;

    const hoverY = HERO_EXC.y + 1.55;
    const contactY = HERO_EXC.y + 0.13 + 0.07; // resting on the cube
    const y = lerp(lerp(hoverY + 1.8, hoverY, enter), contactY, descend) + lift * 0.9 + fade * 1.2;
    group.current.position.set(HERO_EXC.x, y + Math.sin(t * 0.8) * 0.03 * (1 - descend), HERO_EXC.z);
    group.current.rotation.y = t * 0.25 * (1 - descend) + Math.PI * 0.25;
    group.current.rotation.z = (1 - enter) * 0.4;
    group.current.scale.setScalar(Math.max(0.0001, 0.9 * vis));

    const impact = seg(p, 3.5, 3.56);
    const wave = clamp01((p - 3.5) / 0.42);
    ring.current.visible = wave > 0 && wave < 1;
    ring2.current.visible = ring.current.visible;
    const ws = 0.4 + wave * 3.6;
    ring.current.scale.set(ws, ws, ws);
    ring.current.material.opacity = (1 - wave) * 0.85;
    const ws2 = 0.3 + wave * 2.2;
    ring2.current.scale.set(ws2, ws2, ws2);
    ring2.current.material.opacity = (1 - wave) * 0.5;
    ring.current.position.set(HERO_EXC.x, HERO_EXC.y + 0.14, HERO_EXC.z);
    ring2.current.position.copy(ring.current.position);

    const glowPulse = 0.85 + Math.sin(t * 1.6) * 0.15;
    light.current.intensity = (4 + impact * 40 * (1 - wave * 0.8)) * vis * glowPulse;
    flare.current.material.opacity = (0.2 + impact * 0.55 * (1 - wave * 0.7)) * vis;
    const fs = 1.6 + impact * 2.2 * (1 - wave * 0.5);
    flare.current.scale.set(fs, fs, 1);
    markMat.current.emissiveIntensity = (0.7 + impact * 1.2) * glowPulse;
  });

  return (
    <group>
      <group ref={group}>
        {/* handle */}
        <mesh position={[0, 0.62, 0]}>
          <cylinderGeometry args={[0.1, 0.16, 0.7, 24]} />
          <meshStandardMaterial color="#1a1f27" metalness={0.92} roughness={0.42} />
        </mesh>
        <mesh position={[0, 1.02, 0]}>
          <sphereGeometry args={[0.13, 24, 24]} />
          <meshStandardMaterial color="#22282f" metalness={0.95} roughness={0.35} />
        </mesh>
        {/* disc */}
        <mesh position={[0, 0.2, 0]}>
          <cylinderGeometry args={[0.46, 0.5, 0.14, 48]} />
          <meshStandardMaterial color="#141922" metalness={0.9} roughness={0.32} />
        </mesh>
        {/* gold mark on the face */}
        <mesh position={[0, 0.12, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.32, 0.028, 16, 64]} />
          <meshStandardMaterial ref={markMat} color={GOLD_DEEP} emissive={GOLD} emissiveIntensity={1} metalness={0.85} roughness={0.3} />
        </mesh>
        <mesh position={[-0.09, 0.12, 0]} rotation={[0, 0, 0.42]}>
          <boxGeometry args={[0.04, 0.4, 0.04]} />
          <meshStandardMaterial color={GOLD_DEEP} emissive={GOLD} emissiveIntensity={1} metalness={0.85} roughness={0.3} />
        </mesh>
        <mesh position={[0.09, 0.12, 0]} rotation={[0, 0, -0.42]}>
          <boxGeometry args={[0.04, 0.4, 0.04]} />
          <meshStandardMaterial color={GOLD_DEEP} emissive={GOLD} emissiveIntensity={1} metalness={0.85} roughness={0.3} />
        </mesh>
        <sprite ref={flare} position={[0, 0.1, 0]}>
          <spriteMaterial map={tex} color={GOLD} transparent opacity={0.3} depthWrite={false} blending={THREE.AdditiveBlending} />
        </sprite>
        <pointLight ref={light} color={GOLD} distance={9} decay={2} intensity={0} position={[0, 0.3, 0.4]} />
      </group>
      {/* shockwave rings at the point of contact */}
      <mesh ref={ring} rotation={[Math.PI / 2, 0, 0]} visible={false}>
        <ringGeometry args={[0.92, 1, 96]} />
        <meshBasicMaterial color={GOLD} transparent opacity={0} side={THREE.DoubleSide} depthWrite={false} blending={THREE.AdditiveBlending} />
      </mesh>
      <mesh ref={ring2} rotation={[Math.PI / 2, 0, 0]} visible={false}>
        <ringGeometry args={[0.96, 1, 96]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0} side={THREE.DoubleSide} depthWrite={false} blending={THREE.AdditiveBlending} />
      </mesh>
    </group>
  );
}

/* ------------------------------------------------------------------ LP report (output) */
const REPORT_LINES = [
  [0.55, -0.62, 0.9],
  [0.36, -0.42, 0.6],
  [0.24, -0.36, 1.1],
  [0.12, -0.4, 0.85],
  [0.0, -0.44, 1.0],
  [-0.12, -0.5, 0.7],
  [-0.24, -0.38, 1.05],
  [-0.4, -0.5, 0.75],
];

function Report() {
  const group = useRef();
  const edge = useRef();
  const light = useRef();
  const sealMat = useRef();
  const tex = useGlowTexture();
  const glow = useRef();

  useFrame(({ clock, viewport }) => {
    if (!group.current) return;
    const p = stageState.p;
    const t = T(clock);
    const rise = seg(p, 3.95, 4.7);
    const settle = seg(p, 4.55, 4.95);
    group.current.visible = rise > 0.002;
    // keep the page clear of the chapter copy: right of centre on wide screens, above it on narrow
    const wide = viewport.aspect > 1.1;
    const restX = wide ? 1.35 : 0.1;
    const restY = wide ? 0.35 : 0.55;
    const restScale = wide ? 1 : 0.72;
    group.current.position.set(
      lerp(0.6, restX, rise),
      lerp(-4.2, restY, rise) + Math.sin(t * 0.6) * 0.03 * settle,
      lerp(-1.5, 0.4, rise)
    );
    group.current.rotation.x = lerp(-0.65, 0, rise) - 0.06 * settle;
    group.current.rotation.y = lerp(0.55, 0, rise) + Math.sin(t * 0.4) * 0.03 * settle;
    group.current.rotation.z = lerp(-0.12, 0, rise);
    const s = (0.7 + 0.3 * rise) * restScale;
    group.current.scale.setScalar(Math.max(0.0001, s));
    edge.current.material.opacity = 0.22 + 0.28 * settle;
    light.current.intensity = (6 + 18 * settle) * rise;
    sealMat.current.emissiveIntensity = settle * 1.1 * (0.85 + Math.sin(t * 1.4) * 0.15);
    glow.current.material.opacity = 0.2 * rise + 0.3 * settle;
  });

  return (
    <group ref={group}>
      {/* gold light edge behind the page */}
      <mesh ref={edge} position={[0, 0, -0.03]}>
        <planeGeometry args={[1.52, 2.02]} />
        <meshBasicMaterial color={GOLD} transparent opacity={0.4} depthWrite={false} />
      </mesh>
      {/* page */}
      <mesh>
        <boxGeometry args={[1.48, 1.98, 0.03]} />
        <meshPhysicalMaterial color="#0e1116" roughness={0.3} metalness={0.15} clearcoat={0.7} clearcoatRoughness={0.25} />
      </mesh>
      {/* header rule */}
      <mesh position={[-0.18, 0.78, 0.02]}>
        <boxGeometry args={[0.9, 0.05, 0.01]} />
        <meshStandardMaterial color="#c4ccd8" emissive="#c4ccd8" emissiveIntensity={0.25} />
      </mesh>
      <mesh position={[0.44, 0.78, 0.02]}>
        <boxGeometry args={[0.3, 0.05, 0.01]} />
        <meshStandardMaterial color={GOLD} emissive={GOLD} emissiveIntensity={0.7} />
      </mesh>
      {/* lines */}
      {REPORT_LINES.map(([y, x, w], i) => (
        <mesh key={i} position={[x + w / 2, y, 0.02]}>
          <boxGeometry args={[w, 0.028, 0.01]} />
          <meshStandardMaterial color="#3a424e" emissive="#3a424e" emissiveIntensity={0.15} />
        </mesh>
      ))}
      {/* totals block */}
      <mesh position={[0.25, -0.64, 0.02]}>
        <boxGeometry args={[0.7, 0.028, 0.01]} />
        <meshStandardMaterial color="#8a95a6" emissive="#8a95a6" emissiveIntensity={0.25} />
      </mesh>
      {/* seal mark bottom-right */}
      <mesh position={[0.5, -0.8, 0.03]}>
        <torusGeometry args={[0.1, 0.012, 12, 48]} />
        <meshStandardMaterial ref={sealMat} color={GOLD_DEEP} emissive={GOLD} emissiveIntensity={0} metalness={0.85} roughness={0.3} />
      </mesh>
      <sprite ref={glow} position={[0.5, -0.8, 0.05]} scale={[0.9, 0.9, 1]}>
        <spriteMaterial map={tex} color={GOLD} transparent opacity={0} depthWrite={false} blending={THREE.AdditiveBlending} />
      </sprite>
      <pointLight ref={light} color={GOLD} position={[1.2, 1.4, 1.6]} distance={8} decay={2} intensity={0} />
    </group>
  );
}

/* ------------------------------------------------------------------ lights + scene */
function KeyLights() {
  const spot = useRef();
  useFrame(({ clock }) => {
    if (!spot.current) return;
    const t = T(clock);
    spot.current.intensity = 70 + Math.sin(t * 0.7) * 6;
  });
  return (
    <>
      <ambientLight intensity={0.22} />
      <hemisphereLight args={["#2a2d33", "#000000", 0.55]} />
      <spotLight ref={spot} color={GOLD} position={[5, 7, 5]} angle={0.55} penumbra={0.9} intensity={70} distance={0} decay={1.6} />
      <directionalLight color="#9fb0c9" position={[-6, 2, 4]} intensity={0.9} />
    </>
  );
}

export const Scene = () => {
  return (
    <>
      <fogExp2 attach="fog" args={["#000000", 0.042]} />
      <KeyLights />
      <CameraRig />
      <Dust />
      <Fragments />
      <Core />
      <Stream k={0} />
      <Stream k={1} />
      <Stream k={2} />
      <Exceptions />
      <Seal />
      <Report />
    </>
  );
};
